import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useData } from "../../contexts/DataContext";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isWeekend, isToday } from "date-fns";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";

// Safe date converter (handles Firestore Timestamps)





export default function ParentAttendance() {
  const { userData } = useAuth();
  const { getAttendance } = useData();
  const [att, setAtt]   = useState([]);
  const [loading, setLoading] = useState(true);
  const childId = userData?.children?.[0] || null;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { getAttendance({ studentId: childId }).then(d => { setAtt(d); setLoading(false); }); }, []);

  const present = att.filter(a=>a.status==="present").length;
  const absent  = att.filter(a=>a.status==="absent").length;
  const late    = att.filter(a=>a.status==="late").length;
  const total   = att.length;
  const pct     = total>0?Math.round((present/total)*100):0;

  const pieData = [
    {name:"Present",value:present,color:"#059669"},
    {name:"Absent", value:absent, color:"#dc2626"},
    {name:"Late",   value:late,   color:"#d97706"},
  ].filter(d=>d.value>0);

  const now  = new Date();
  const days = eachDayOfInterval({ start: startOfMonth(now), end: endOfMonth(now) });
  const getDay = (d) => att.find(a => a.date === format(d,"yyyy-MM-dd"));

  const STATUS = {
    present:{bg:"#ecfdf5",border:"#a7f3d0",text:"#059669",label:"P"},
    absent: {bg:"#fef2f2",border:"#fca5a5",text:"#dc2626",label:"A"},
    late:   {bg:"#fffbeb",border:"#fde68a",text:"#d97706",label:"L"},
  };

  return (
    <div className="fade-in">
      <div className="page-head">
        <div>
          <h1 className="page-title">✅ Child's Attendance</h1>
          <p className="page-subtitle">Monitoring: <strong>{userData?.childName||"Aarav Poudel"}</strong></p>
        </div>
      </div>

      <div className="g g4" style={{ marginBottom: 18 }}>
        {[
          {label:"Present",value:present,color:"var(--green-600)"},
          {label:"Absent", value:absent, color:"var(--red-600)"},
          {label:"Late",   value:late,   color:"var(--amber-600)"},
          {label:"Rate",   value:`${pct}%`,color:pct>=75?"var(--green-600)":"var(--red-600)"},
        ].map(s=>(
          <div key={s.label} className="card" style={{padding:"14px 16px",textAlign:"center"}}>
            <div style={{fontSize:"1.6rem",fontWeight:800,color:s.color,fontFamily:"Poppins,sans-serif"}}>{s.value}</div>
            <div style={{fontSize:".76rem",color:"var(--gray-500)",marginTop:3}}>{s.label}</div>
          </div>
        ))}
      </div>

      {pct < 75 && (
        <div style={{ background:"var(--red-50)",border:"1px solid var(--red-100)",borderRadius:"var(--r8)",padding:"12px 16px",marginBottom:16,display:"flex",gap:10,alignItems:"flex-start" }}>
          <span style={{fontSize:"1.2rem"}}>⚠️</span>
          <div>
            <div style={{fontWeight:700,color:"var(--red-600)",fontSize:".88rem"}}>Attendance Warning</div>
            <div style={{fontSize:".82rem",color:"var(--red-600)",marginTop:2}}>Your child's attendance ({pct}%) is below the required 75%. Please ensure they attend school regularly.</div>
          </div>
        </div>
      )}

      <div style={{display:"grid",gridTemplateColumns:"1fr 260px",gap:16,alignItems:"start"}}>
        {/* Calendar */}
        <div className="card">
          <div className="card-header"><h3 className="card-title">📅 {format(now,"MMMM yyyy")}</h3></div>
          <div className="card-body">
            {loading ? <div className="loading-center"><div className="spinner"/></div> : (
              <>
                <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:4,marginBottom:6}}>
                  {["Su","Mo","Tu","We","Th","Fr","Sa"].map(d=><div key={d} style={{textAlign:"center",fontSize:".68rem",fontWeight:700,color:"var(--gray-400)",padding:"3px 0"}}>{d}</div>)}
                </div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:4}}>
                  {Array(days[0].getDay()).fill(null).map((_,i)=><div key={i}/>)}
                  {days.map(day=>{
                    const rec=getDay(day);
                    const st=rec?STATUS[rec.status]:null;
                    const today=isToday(day);
                    const wkend=isWeekend(day);
                    return (
                      <div key={day.toISOString()} style={{borderRadius:"var(--r2)",padding:"5px 3px",textAlign:"center",background:st?st.bg:wkend?"var(--gray-50)":"#fff",border:`1px solid ${st?st.border:today?"var(--blue-700)":"#f0f0f0"}`,outline:today?"2px solid var(--blue-100)":"none"}}>
                        <div style={{fontSize:".76rem",fontWeight:today?700:400,color:st?st.text:wkend?"var(--gray-400)":"var(--gray-700)"}}>{format(day,"d")}</div>
                        {st&&<div style={{fontSize:".58rem",fontWeight:800,color:st.text}}>{st.label}</div>}
                      </div>
                    );
                  })}
                </div>
                <div style={{display:"flex",gap:12,marginTop:12,flexWrap:"wrap"}}>
                  {Object.entries(STATUS).map(([k,v])=>(
                    <div key={k} style={{display:"flex",alignItems:"center",gap:4}}>
                      <div style={{width:11,height:11,borderRadius:2,background:v.bg,border:`1px solid ${v.border}`}}/>
                      <span style={{fontSize:".7rem",color:"var(--gray-500)"}}>{k.charAt(0).toUpperCase()+k.slice(1)}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Pie */}
        <div className="card">
          <div className="card-header"><h3 className="card-title">Summary</h3></div>
          <div className="card-body" style={{textAlign:"center"}}>
            <div style={{fontSize:"2.2rem",fontWeight:800,color:pct>=75?"var(--green-600)":"var(--red-600)",fontFamily:"Poppins,sans-serif"}}>{pct}%</div>
            <div style={{fontSize:".78rem",color:"var(--gray-500)",marginBottom:12}}>Attendance Rate</div>
            {total > 0 && (
              <ResponsiveContainer width="100%" height={120}>
                <PieChart><Pie data={pieData} dataKey="value" cx="50%" cy="50%" innerRadius={32} outerRadius={52}>
                  {pieData.map((e,i)=><Cell key={i} fill={e.color}/>)}
                </Pie><Tooltip contentStyle={{fontSize:".76rem",borderRadius:8}}/></PieChart>
              </ResponsiveContainer>
            )}
            <div style={{marginTop:12}}>
              {pieData.map(d=>(
                <div key={d.name} style={{display:"flex",justifyContent:"space-between",padding:"5px 0",borderBottom:"1px solid #f0f0f0"}}>
                  <div style={{display:"flex",alignItems:"center",gap:6}}><div style={{width:9,height:9,borderRadius:"50%",background:d.color}}/><span style={{fontSize:".8rem"}}>{d.name}</span></div>
                  <span style={{fontWeight:700,color:d.color,fontSize:".8rem"}}>{d.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <style>{`@media(max-width:900px){div[style*="grid-template-columns:1fr 260px"]{grid-template-columns:1fr!important}}`}</style>

    </div>
  );
}